import { type ChangeEvent, type FocusEvent, useRef } from 'react'
import { Box } from '@mui/material'
import { TextField as DhlTextField, type TextFieldMediumLargeProps, Body2 } from '@dhl-ui/components'
import { type WithRequired } from './types'
import { FastField, type FastFieldProps, shouldUpdate, generateUIDX, calculateLength } from './shared'

export type TextFieldInputProps = WithRequired<TextFieldMediumLargeProps, 'name'> & {
  transformBeforeChange?: (e: ChangeEvent<HTMLInputElement>) => string
  hideMaxLengthIndicator?: boolean
}

/** @deprecated: move towards react-hook-form */
export const TextFieldInput = ({
  helperText,
  name,
  autoComplete,
  transformBeforeChange,
  hideMaxLengthIndicator = false,
  ...props
}: TextFieldInputProps) => {
  const random = generateUIDX()
  const inputRef = useRef<HTMLInputElement>(null)

  // Chrome ignores autoComplete="off" ...
  const fieldName = autoComplete === 'off' ? `${name}-${random}` : name
  const fieldAutocomplete = autoComplete === 'off' ? `off-${random}` : autoComplete

  // TypeScript complains about maxLength not being a valid property
  // reason: Property 'maxLength' does not exist on type 'SlotProps<ElementType<InputBaseComponentProps | undefined, keyof IntrinsicElements>, {}, BaseTextFieldProps>'.
  // @ts-expect-error
  const maxLength = props.slotProps?.htmlInput?.maxLength

  return (
    <FastField {...props} name={name} shouldUpdate={shouldUpdate}>
      {({ field, meta, form }: FastFieldProps) => (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          <DhlTextField
            {...props}
            {...field}
            name={fieldName}
            id={fieldName}
            autoComplete={fieldAutocomplete}
            inputRef={inputRef}
            /**
             * what the hell is all this code?
             */
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              // Update the touched flag, but don't trigger a validation. If needed, we will trigger a validation
              // on the `setValue`. We don't want to trigger two validations. This is also the reason
              // why we don't call `field.onBlur` here.
              form.setFieldTouched(name, true, false)

              // Update the value with the trimmed input and trigger a validation if validateOnBlur equals true
              form.setFieldValue(name, e.target.value?.trim(), form.validateOnBlur)
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              // Restore name attribute. Needed if the name was randomized to fix autoComplete="off"
              e.target.name = name

              // Only replace the target value if the method is actually defined. Setting the target value
              // to a new value causes a strange issue in iOS Safari. When you move the cursor to another spot
              // in something you typed and then start typing again, the cursor is moved to the end of the input.
              // Not an ideal fix, but at least this significantly reduces the change of it occurring, since this
              // prop is only used in a few places.
              if (transformBeforeChange) {
                e.target.value = transformBeforeChange(e)
              }

              field.onChange(e)
            }}
            error={!!meta.error && meta.touched}
            helperText={meta.touched && meta.error?.trim() ? meta.error : helperText}
            slotProps={{
              ...props.slotProps,
              htmlInput: {
                ...props.slotProps?.htmlInput,
                'data-testid': name,
              },
            }}
          />
          {maxLength && !hideMaxLengthIndicator && (
            <Body2>{`${field.value ? calculateLength(field.value) : 0}/${maxLength}`}</Body2>
          )}
        </Box>
      )}
    </FastField>
  )
}
